import React, { useState, useEffect } from 'react';
import { 
  Lock, LogOut, Users, Building2, TrendingUp, Calendar,
  Download, Trash2, Filter, Search, ArrowLeft, Eye, 
  UserPlus, Settings, Shield, UserCheck, Key, Building,
  CheckCircle, XCircle, Database, Plus, Briefcase, FileText,
  Target, BarChart3, Flag, Clock, ArrowUpRight, ArrowDownRight, Beaker, Globe
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState('leads');
  
  // User management
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreatePortalUser, setShowCreatePortalUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'supervisor', email: '' });
  const [newPortalUser, setNewPortalUser] = useState({ email: '', name: '', role: 'investor' });
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  
  // Portal Data Management
  const [selectedPortalUser, setSelectedPortalUser] = useState(null);
  const [dataType, setDataType] = useState('investment');
  const [showAddDataModal, setShowAddDataModal] = useState(false);
  const [dataFormLoading, setDataFormLoading] = useState(false);
  
  // Form states for different data types
  const [investmentForm, setInvestmentForm] = useState({
    company_name: '', amount: '', date: '', status: 'active', returns_percentage: '', notes: '', currency: 'EUR'
  });
  const [dealForm, setDealForm] = useState({
    title: '', company_name: '', sector: '', stage: '', target_raise: '', minimum_ticket: '', description: '', status: 'open', deadline: ''
  });
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', due_date: '', priority: 'medium'
  });
  const [milestoneForm, setMilestoneForm] = useState({
    title: '', description: '', due_date: '', status: 'pending', progress_percentage: 0
  });
  const [kpiForm, setKpiForm] = useState({
    name: '', value: '', target: '', unit: '', period: '', trend: 'stable'
  });
  const [documentForm, setDocumentForm] = useState({
    filename: '', category: 'report', description: '', file_type: 'pdf'
  });
  const [meetingForm, setMeetingForm] = useState({
    title: '', description: '', date: '', time: '', duration_minutes: 60, location: '', meeting_link: ''
  });
  
  // Funnel Analytics
  const [funnelAnalytics, setFunnelAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('all_time');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // Trend & Comparison
  const [trendData, setTrendData] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  
  // A/B Testing
  const [abTests, setAbTests] = useState([]);
  const [selectedAbTest, setSelectedAbTest] = useState(null);
  const [abTestResults, setAbTestResults] = useState(null);
  
  // CRM Webhook
  const [crmWebhookUrl, setCrmWebhookUrl] = useState('');
  const [crmType, setCrmType] = useState('generic');
  const [crmTestResult, setCrmTestResult] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, { username, password });
      setIsAuthenticated(true);
      setToken(response.data.token);
      setCurrentUser(response.data.user);
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.user));
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/logout?token=${token}`);
    } catch (e) {}
    setIsAuthenticated(false);
    setToken('');
    setCurrentUser(null);
    setPassword('');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  };

  // Check for stored auth on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setCurrentUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchStats();
      fetchLeads();
      if (isAdmin) {
        fetchUsers();
      }
    }
  }, [isAuthenticated, token, filterType, isAdmin]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchLeads = async () => {
    try {
      const typeParam = filterType !== 'all' ? `&lead_type=${filterType}` : '';
      const response = await axios.get(`${API_URL}/api/admin/leads?token=${token}${typeParam}`);
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users?token=${token}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/leads/${leadId}?token=${token}`);
      fetchLeads();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const createUser = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/users?token=${token}`, newUser);
      setShowCreateUser(false);
      setNewUser({ username: '', password: '', role: 'supervisor', email: '' });
      fetchUsers();
      alert('User created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const deleteUser = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${username}?token=${token}`);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetPassword = async (username) => {
    if (!window.confirm(`Reset password for "${username}"?`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${username}/reset-password?token=${token}`);
      setResetPasswordResult({ username, password: response.data.new_password });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Funnel Analytics
  const fetchFunnelAnalytics = async (period = analyticsPeriod, customStart = null, customEnd = null) => {
    setAnalyticsLoading(true);
    try {
      let url = `${API_URL}/api/analytics/funnel/summary`;
      const params = new URLSearchParams();
      
      if (period === 'custom' && customStart && customEnd) {
        params.append('start_date', customStart);
        params.append('end_date', customEnd);
      } else if (period !== 'all_time') {
        params.append('period', period);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setFunnelAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch funnel analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const exportAnalyticsCSV = async () => {
    try {
      let url = `${API_URL}/api/analytics/funnel/export`;
      const params = new URLSearchParams();
      
      if (analyticsPeriod === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('start_date', customDateRange.start);
        params.append('end_date', customDateRange.end);
      } else if (analyticsPeriod !== 'all_time') {
        params.append('period', analyticsPeriod);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `funnel_analytics_${analyticsPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export analytics:', error);
      alert('Failed to export analytics data');
    }
  };

  const handlePeriodChange = (period) => {
    setAnalyticsPeriod(period);
    if (period !== 'custom') {
      fetchFunnelAnalytics(period);
    }
  };

  const handleCustomDateSearch = () => {
    if (customDateRange.start && customDateRange.end) {
      fetchFunnelAnalytics('custom', customDateRange.start, customDateRange.end);
    }
  };

  // Trend Data
  const fetchTrendData = async (days = trendDays) => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/funnel/trend?days=${days}`);
      setTrendData(response.data.trend || []);
    } catch (error) {
      console.error('Failed to fetch trend data:', error);
    }
  };

  // Period Comparison
  const fetchComparison = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/funnel/comparison`);
      setComparison(response.data);
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
    }
  };

  // A/B Tests
  const fetchAbTests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/ab-tests`);
      setAbTests(response.data.tests || []);
    } catch (error) {
      console.error('Failed to fetch A/B tests:', error);
    }
  };

  const fetchAbTestResults = async (testId) => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/ab-test/${testId}/results`);
      setAbTestResults(response.data);
    } catch (error) {
      console.error('Failed to fetch A/B test results:', error);
    }
  };

  // CRM Webhook Test
  const testCrmWebhook = async () => {
    if (!crmWebhookUrl) return;
    setCrmTestResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/crm/webhook/test`, {
        crm_type: crmType,
        webhook_url: crmWebhookUrl
      });
      setCrmTestResult(response.data);
    } catch (error) {
      setCrmTestResult({ success: false, error: error.message });
    }
  };

  // Portal User Management
  const fetchPortalUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portal/admin/portal-users`, {
        headers: { 'X-Admin-Token': token }
      });
      setPortalUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch portal users:', error);
    }
  };

  const createPortalUser = async () => {
    try {
      await axios.post(`${API_URL}/api/portal/admin/users`, newPortalUser, {
        headers: { 'X-Admin-Token': token }
      });
      setShowCreatePortalUser(false);
      setNewPortalUser({ email: '', name: '', role: 'investor' });
      fetchPortalUsers();
      alert('Portal user created successfully! They can now login with Google.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create portal user');
    }
  };

  const updatePortalUserStatus = async (userId, status) => {
    try {
      await axios.put(`${API_URL}/api/portal/admin/portal-users/${userId}/status?status=${status}`, {}, {
        headers: { 'X-Admin-Token': token }
      });
      fetchPortalUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Portal Data Management Functions
  const resetForms = () => {
    setInvestmentForm({ company_name: '', amount: '', date: '', status: 'active', returns_percentage: '', notes: '', currency: 'EUR' });
    setDealForm({ title: '', company_name: '', sector: '', stage: '', target_raise: '', minimum_ticket: '', description: '', status: 'open', deadline: '' });
    setTaskForm({ title: '', description: '', due_date: '', priority: 'medium' });
    setMilestoneForm({ title: '', description: '', due_date: '', status: 'pending', progress_percentage: 0 });
    setKpiForm({ name: '', value: '', target: '', unit: '', period: '', trend: 'stable' });
    setDocumentForm({ filename: '', category: 'report', description: '', file_type: 'pdf' });
    setMeetingForm({ title: '', description: '', date: '', time: '', duration_minutes: 60, location: '', meeting_link: '' });
  };

  const addPortalData = async () => {
    if (!selectedPortalUser) {
      alert('Please select a user first');
      return;
    }
    
    setDataFormLoading(true);
    try {
      let endpoint = '';
      let payload = { user_id: selectedPortalUser.user_id };
      
      switch (dataType) {
        case 'investment':
          endpoint = '/api/portal/admin/investments';
          payload = { ...payload, ...investmentForm, amount: parseFloat(investmentForm.amount) || 0, returns_percentage: investmentForm.returns_percentage ? parseFloat(investmentForm.returns_percentage) : null };
          break;
        case 'deal':
          endpoint = '/api/portal/admin/deals';
          payload = { ...dealForm, target_raise: parseFloat(dealForm.target_raise) || 0, minimum_ticket: parseFloat(dealForm.minimum_ticket) || 0 };
          break;
        case 'task':
          endpoint = '/api/portal/admin/tasks';
          payload = { ...payload, ...taskForm };
          break;
        case 'milestone':
          endpoint = '/api/portal/admin/milestones';
          payload = { ...payload, ...milestoneForm, progress_percentage: parseInt(milestoneForm.progress_percentage) || 0 };
          break;
        case 'kpi':
          endpoint = '/api/portal/admin/kpis';
          payload = { ...payload, ...kpiForm, value: parseFloat(kpiForm.value) || 0, target: parseFloat(kpiForm.target) || 0 };
          break;
        case 'document':
          endpoint = '/api/portal/admin/documents';
          payload = { ...payload, ...documentForm };
          break;
        case 'meeting':
          endpoint = '/api/portal/admin/meetings';
          payload = { ...payload, ...meetingForm, duration_minutes: parseInt(meetingForm.duration_minutes) || 60 };
          break;
        default:
          throw new Error('Invalid data type');
      }
      
      await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: { 'X-Admin-Token': token }
      });
      
      setShowAddDataModal(false);
      resetForms();
      alert(`${dataType.charAt(0).toUpperCase() + dataType.slice(1)} added successfully!`);
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to add ${dataType}`);
    } finally {
      setDataFormLoading(false);
    }
  };

  // Fetch portal users when tab changes
  useEffect(() => {
    if (isAuthenticated && token && isAdmin && (activeTab === 'portal' || activeTab === 'portaldata')) {
      fetchPortalUsers();
    }
  }, [isAuthenticated, token, isAdmin, activeTab]);

  const exportToCSV = () => {
    const headers = [
      'ID', 'Type', 'First Name', 'Last Name', 'Email', 'Phone', 
      'Country', 'Company', 'Role', 'Created At'
    ];
    
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        lead.id,
        lead.lead_type,
        lead['contact.first_name'],
        lead['contact.last_name'],
        lead['contact.email'],
        lead['contact.phone'] || '',
        lead['contact.country'],
        lead['contact.company'] || '',
        lead['contact.role'] || '',
        lead.created_at
      ].map(field => `"${field || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hubner-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead['contact.first_name']?.toLowerCase().includes(search) ||
      lead['contact.last_name']?.toLowerCase().includes(search) ||
      lead['contact.email']?.toLowerCase().includes(search) ||
      lead['contact.company']?.toLowerCase().includes(search)
    );
  });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-navy" />
            </div>
            <CardTitle className="font-heading text-2xl">Admin Access</CardTitle>
            <p className="text-muted-foreground font-body text-sm mt-2">
              Sign in to access the dashboard
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12"
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12"
                  data-testid="admin-password-input"
                />
              </div>
              {loginError && (
                <p className="text-red-500 text-sm">{loginError}</p>
              )}
              <Button 
                type="submit" 
                className="w-full bg-navy text-white hover:bg-navy/90 h-12 mt-4"
                disabled={isLoading}
                data-testid="admin-login-btn"
                style={{ backgroundColor: '#0B1C3E', color: 'white' }}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => window.location.href = '/'}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Website
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-navy text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-xl font-bold">
              HUBNER<span className="text-gold">.</span> Admin
            </h1>
            <span className={`text-xs px-2 py-1 rounded ${
              isAdmin ? 'bg-gold text-navy' : 'bg-white/20'
            }`}>
              {currentUser?.role?.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 text-sm hidden sm:block">
              {currentUser?.username}
            </span>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-body">Total Leads</p>
                    <p className="text-3xl font-heading font-bold">{stats.total_leads}</p>
                  </div>
                  <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-navy" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-body">Investors</p>
                    <p className="text-3xl font-heading font-bold">{stats.investor_leads}</p>
                  </div>
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-gold" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-body">Companies</p>
                    <p className="text-3xl font-heading font-bold">{stats.company_leads}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-body">This Week</p>
                    <p className="text-3xl font-heading font-bold">{stats.leads_this_week}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted">
            <TabsTrigger value="leads" className="data-[state=active]:bg-white">
              <Users className="h-4 w-4 mr-2" />
              Leads
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="portal" className="data-[state=active]:bg-white">
                <UserCheck className="h-4 w-4 mr-2" />
                Portal Users
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="portaldata" className="data-[state=active]:bg-white">
                <Database className="h-4 w-4 mr-2" />
                Portal Data
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="users" className="data-[state=active]:bg-white">
                <Settings className="h-4 w-4 mr-2" />
                Admin Users
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="analytics" className="data-[state=active]:bg-white" onClick={fetchFunnelAnalytics}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Funnel Analytics
              </TabsTrigger>
            )}
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle className="font-heading text-xl">Leads</CardTitle>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search leads..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-full sm:w-64"
                        data-testid="admin-search-input"
                      />
                    </div>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="admin-filter-select">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="investor">Investors</SelectItem>
                        <SelectItem value="company">Companies</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={exportToCSV}
                      variant="outline"
                      data-testid="admin-export-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No leads found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLeads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="font-medium">
                              {lead['contact.first_name']} {lead['contact.last_name']}
                            </TableCell>
                            <TableCell>{lead['contact.email']}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                lead.lead_type === 'investor' 
                                  ? 'bg-gold/20 text-gold' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {lead.lead_type}
                              </span>
                            </TableCell>
                            <TableCell>{lead['contact.company'] || '-'}</TableCell>
                            <TableCell>
                              {new Date(lead.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedLead(lead)}
                                  data-testid={`view-lead-${lead.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteLead(lead.id)}
                                    className="text-red-500 hover:text-red-700"
                                    data-testid={`delete-lead-${lead.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl">User Management</CardTitle>
                    <Button onClick={() => setShowCreateUser(true)} className="bg-navy">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add User
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Username</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.username}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.role === 'admin' 
                                ? 'bg-gold/20 text-gold' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role === 'admin' ? (
                                <span className="flex items-center gap-1">
                                  <Shield className="h-3 w-3" /> Admin
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <UserCheck className="h-3 w-3" /> Supervisor
                                </span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>{user.email || '-'}</TableCell>
                          <TableCell>
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => resetPassword(user.username)}
                                title="Reset Password"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              {user.username !== currentUser?.username && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteUser(user.username)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Portal Users Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="portal">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-xl">Portal Users</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Manage client portal access for investors and companies
                      </p>
                    </div>
                    <Button onClick={() => setShowCreatePortalUser(true)} className="bg-navy">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Portal User
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {portalUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No portal users yet. Add users to give them access to the client portal.
                          </TableCell>
                        </TableRow>
                      ) : (
                        portalUsers.map((user) => (
                          <TableRow key={user.user_id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {user.picture && (
                                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                                )}
                                {user.name}
                              </div>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.role === 'investor' 
                                  ? 'bg-gold/20 text-gold' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {user.role}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.status === 'active' 
                                  ? 'bg-green-100 text-green-700' 
                                  : user.status === 'suspended'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {user.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {user.status === 'active' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updatePortalUserStatus(user.user_id, 'suspended')}
                                    className="text-red-500 hover:text-red-700"
                                    title="Suspend User"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updatePortalUserStatus(user.user_id, 'active')}
                                    className="text-green-600 hover:text-green-700"
                                    title="Activate User"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Portal Data Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="portaldata">
              <Card>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading text-xl">Manage Portal Data</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Add investments, deals, tasks, and more for portal users
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Select User */}
                  <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                    <h3 className="font-heading font-semibold flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-navy text-white text-sm flex items-center justify-center">1</span>
                      Select Portal User
                    </h3>
                    <Select 
                      value={selectedPortalUser?.user_id || ''} 
                      onValueChange={(id) => setSelectedPortalUser(portalUsers.find(u => u.user_id === id))}
                    >
                      <SelectTrigger className="w-full md:w-96">
                        <SelectValue placeholder="Choose a user..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {portalUsers.filter(u => u.status === 'active').map(user => (
                          <SelectItem key={user.user_id} value={user.user_id}>
                            <span className="flex items-center gap-2">
                              {user.name} 
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                user.role === 'investor' ? 'bg-gold/20 text-gold' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {user.role}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedPortalUser && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-navy" />
                        </div>
                        <div>
                          <p className="font-medium">{selectedPortalUser.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedPortalUser.email} • {selectedPortalUser.role}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Step 2: Select Data Type */}
                  {selectedPortalUser && (
                    <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                      <h3 className="font-heading font-semibold flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-navy text-white text-sm flex items-center justify-center">2</span>
                        Choose What to Add
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {selectedPortalUser.role === 'investor' && (
                          <>
                            <DataTypeButton 
                              icon={TrendingUp} 
                              label="Investment" 
                              active={dataType === 'investment'}
                              onClick={() => setDataType('investment')}
                            />
                            <DataTypeButton 
                              icon={Briefcase} 
                              label="Deal" 
                              active={dataType === 'deal'}
                              onClick={() => setDataType('deal')}
                              badge="All Investors"
                            />
                          </>
                        )}
                        {selectedPortalUser.role === 'company' && (
                          <>
                            <DataTypeButton 
                              icon={Flag} 
                              label="Milestone" 
                              active={dataType === 'milestone'}
                              onClick={() => setDataType('milestone')}
                            />
                            <DataTypeButton 
                              icon={Target} 
                              label="Task" 
                              active={dataType === 'task'}
                              onClick={() => setDataType('task')}
                            />
                            <DataTypeButton 
                              icon={BarChart3} 
                              label="KPI" 
                              active={dataType === 'kpi'}
                              onClick={() => setDataType('kpi')}
                            />
                          </>
                        )}
                        <DataTypeButton 
                          icon={FileText} 
                          label="Document" 
                          active={dataType === 'document'}
                          onClick={() => setDataType('document')}
                        />
                        <DataTypeButton 
                          icon={Calendar} 
                          label="Meeting" 
                          active={dataType === 'meeting'}
                          onClick={() => setDataType('meeting')}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Add Data Button */}
                  {selectedPortalUser && (
                    <div className="flex justify-end">
                      <Button 
                        onClick={() => setShowAddDataModal(true)} 
                        className="bg-navy"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add {dataType.charAt(0).toUpperCase() + dataType.slice(1)}
                      </Button>
                    </div>
                  )}

                  {/* Quick Reference - Data Types */}
                  <div className="border-t pt-6">
                    <h4 className="font-heading font-semibold mb-4">Quick Reference</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="p-4 bg-gold/5 rounded-lg border border-gold/20">
                        <h5 className="font-medium text-gold mb-2 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> For Investors
                        </h5>
                        <ul className="space-y-1 text-muted-foreground">
                          <li>• <strong>Investment:</strong> Track their portfolio entries</li>
                          <li>• <strong>Deal:</strong> Share new investment opportunities (visible to all investors)</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                          <Building className="w-4 h-4" /> For Companies
                        </h5>
                        <ul className="space-y-1 text-muted-foreground">
                          <li>• <strong>Milestone:</strong> Growth goals with progress tracking</li>
                          <li>• <strong>Task:</strong> Action items with priorities</li>
                          <li>• <strong>KPI:</strong> Performance metrics with targets</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Funnel Analytics Tab */}
          {isAdmin && (
            <TabsContent value="analytics">
              <Card>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="font-heading text-xl">Funnel Analytics</CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Period Filters */}
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                        {[
                          { value: 'all_time', label: 'All Time' },
                          { value: 'today', label: 'Today' },
                          { value: '7days', label: '7 Days' },
                          { value: '30days', label: '30 Days' },
                          { value: 'custom', label: 'Custom' },
                        ].map(({ value, label }) => (
                          <button
                            key={value}
                            onClick={() => handlePeriodChange(value)}
                            className={`px-3 py-1.5 text-sm font-body rounded-md transition-colors ${
                              analyticsPeriod === value 
                                ? 'bg-white text-[#1A3A4A] shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Export Button */}
                      <Button variant="outline" size="sm" onClick={exportAnalyticsCSV} className="gap-2">
                        <Download className="h-4 w-4" />
                        Export CSV
                      </Button>
                      
                      {/* Refresh Button */}
                      <Button variant="outline" size="sm" onClick={() => fetchFunnelAnalytics()} disabled={analyticsLoading}>
                        {analyticsLoading ? 'Loading...' : 'Refresh'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Custom Date Range */}
                  {analyticsPeriod === 'custom' && (
                    <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">From:</label>
                        <Input
                          type="date"
                          value={customDateRange.start}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="w-auto"
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">To:</label>
                        <Input
                          type="date"
                          value={customDateRange.end}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="w-auto"
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <Button 
                        size="sm" 
                        onClick={handleCustomDateSearch}
                        disabled={!customDateRange.start || !customDateRange.end}
                      >
                        Apply Filter
                      </Button>
                      <span className="text-xs text-gray-500">(Max 3 years range)</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {funnelAnalytics ? (
                    <div className="space-y-6">
                      {/* Funnel Steps */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                          <p className="text-blue-600 font-body text-sm mb-2">Step 1 Completed</p>
                          <p className="font-heading text-3xl font-bold text-blue-900">{funnelAnalytics.step_1_completed}</p>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 text-center">
                          <p className="text-indigo-600 font-body text-sm mb-2">Step 2 Completed</p>
                          <p className="font-heading text-3xl font-bold text-indigo-900">{funnelAnalytics.step_2_completed}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center">
                          <p className="text-green-600 font-body text-sm mb-2">Form Submitted</p>
                          <p className="font-heading text-3xl font-bold text-green-900">{funnelAnalytics.form_submitted}</p>
                        </div>
                        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 text-center">
                          <p className="text-teal-600 font-body text-sm mb-2">Calendar Booked</p>
                          <p className="font-heading text-3xl font-bold text-teal-900">{funnelAnalytics.calendar_booked}</p>
                        </div>
                      </div>

                      {/* Conversion Rates */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <h3 className="font-heading font-semibold mb-4">Conversion Rates</h3>
                        <div className="space-y-4">
                          {/* Step 1 to Step 2 */}
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="font-body text-sm text-gray-600">Step 1 → Step 2</span>
                              <span className="font-body text-sm font-medium">{funnelAnalytics.step_1_to_step_2_rate}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-blue-500 h-3 rounded-full transition-all"
                                style={{ width: `${funnelAnalytics.step_1_to_step_2_rate}%` }}
                              />
                            </div>
                          </div>
                          {/* Step 2 to Submit */}
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="font-body text-sm text-gray-600">Step 2 → Submit</span>
                              <span className="font-body text-sm font-medium">{funnelAnalytics.step_2_to_submit_rate}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-indigo-500 h-3 rounded-full transition-all"
                                style={{ width: `${funnelAnalytics.step_2_to_submit_rate}%` }}
                              />
                            </div>
                          </div>
                          {/* Submit to Calendar */}
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="font-body text-sm text-gray-600">Submit → Calendar</span>
                              <span className="font-body text-sm font-medium">{funnelAnalytics.submit_to_calendar_rate}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-teal-500 h-3 rounded-full transition-all"
                                style={{ width: `${funnelAnalytics.submit_to_calendar_rate}%` }}
                              />
                            </div>
                          </div>
                          {/* Overall Conversion */}
                          <div className="pt-4 border-t">
                            <div className="flex justify-between mb-1">
                              <span className="font-body text-sm font-semibold text-gray-800">Overall Conversion Rate</span>
                              <span className="font-body text-sm font-bold text-green-600">{funnelAnalytics.overall_conversion_rate}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div 
                                className="bg-gradient-to-r from-green-400 to-green-600 h-4 rounded-full transition-all"
                                style={{ width: `${funnelAnalytics.overall_conversion_rate}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Trend Chart */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-heading font-semibold">Funnel Trend</h3>
                          <div className="flex items-center gap-2">
                            <Select value={String(trendDays)} onValueChange={(v) => { setTrendDays(Number(v)); fetchTrendData(Number(v)); }}>
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="14">Last 14 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="60">Last 60 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button variant="outline" size="sm" onClick={() => fetchTrendData(trendDays)}>
                              Load Chart
                            </Button>
                          </div>
                        </div>
                        {trendData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={trendData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                              <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12 }} 
                                tickFormatter={(value) => value.slice(5)}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip 
                                contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                              />
                              <Legend />
                              <Area type="monotone" dataKey="step_1" name="Step 1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} />
                              <Area type="monotone" dataKey="step_2" name="Step 2" stroke="#6366F1" fill="#6366F1" fillOpacity={0.2} />
                              <Area type="monotone" dataKey="submitted" name="Submitted" stroke="#10B981" fill="#10B981" fillOpacity={0.2} />
                              <Area type="monotone" dataKey="booked" name="Calendar" stroke="#14B8A6" fill="#14B8A6" fillOpacity={0.2} />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-[300px] flex items-center justify-center text-gray-400">
                            <div className="text-center">
                              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>Click "Load Chart" to view trend data</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Period Comparison */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-heading font-semibold">This Week vs Last Week</h3>
                          <Button variant="outline" size="sm" onClick={fetchComparison}>
                            Load Comparison
                          </Button>
                        </div>
                        {comparison ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                              { key: 'step_1', label: 'Step 1', color: 'blue' },
                              { key: 'step_2', label: 'Step 2', color: 'indigo' },
                              { key: 'submitted', label: 'Submitted', color: 'green' },
                              { key: 'booked', label: 'Calendar', color: 'teal' },
                            ].map(({ key, label, color }) => (
                              <div key={key} className={`bg-${color}-50 rounded-xl p-4`}>
                                <p className={`text-${color}-600 font-body text-sm mb-1`}>{label}</p>
                                <div className="flex items-end gap-2">
                                  <span className="font-heading text-2xl font-bold">{comparison.this_week[key]}</span>
                                  <span className="text-gray-400 text-sm">vs {comparison.last_week[key]}</span>
                                </div>
                                <div className={`flex items-center gap-1 mt-1 ${comparison.changes[key] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {comparison.changes[key] >= 0 ? (
                                    <ArrowUpRight className="w-4 h-4" />
                                  ) : (
                                    <ArrowDownRight className="w-4 h-4" />
                                  )}
                                  <span className="text-sm font-medium">{Math.abs(comparison.changes[key])}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-32 flex items-center justify-center text-gray-400">
                            <p>Click "Load Comparison" to compare periods</p>
                          </div>
                        )}
                      </div>

                      {/* A/B Testing Section */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Beaker className="w-5 h-5 text-purple-600" />
                            <h3 className="font-heading font-semibold">A/B Testing</h3>
                          </div>
                          <Button variant="outline" size="sm" onClick={fetchAbTests}>
                            Load Tests
                          </Button>
                        </div>
                        {abTests.length > 0 ? (
                          <div className="space-y-3">
                            {abTests.map((test) => (
                              <div 
                                key={test._id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                                onClick={() => { setSelectedAbTest(test._id); fetchAbTestResults(test._id); }}
                              >
                                <div>
                                  <p className="font-medium">{test._id}</p>
                                  <p className="text-sm text-gray-500">{test.total_events} events</p>
                                </div>
                                <Eye className="w-4 h-4 text-gray-400" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <Beaker className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No A/B tests yet. Tests are automatically tracked when using variant parameters in the funnel.</p>
                          </div>
                        )}
                        
                        {/* A/B Test Results Modal */}
                        {abTestResults && (
                          <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-purple-900">Test: {abTestResults.test_id}</h4>
                              <Button variant="ghost" size="sm" onClick={() => setAbTestResults(null)}>×</Button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              {['A', 'B'].map((variant) => (
                                <div key={variant} className={`p-3 rounded-lg ${abTestResults.winner === variant ? 'bg-green-100 border-2 border-green-500' : 'bg-white'}`}>
                                  <div className="flex items-center justify-between">
                                    <span className="font-bold text-lg">Variant {variant}</span>
                                    {abTestResults.winner === variant && (
                                      <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">Winner</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">Views: {abTestResults.variants[variant].views}</p>
                                  <p className="text-sm text-gray-600">Conversions: {abTestResults.variants[variant].conversions}</p>
                                  <p className="text-lg font-bold text-purple-600">{abTestResults.variants[variant].conversion_rate}%</p>
                                </div>
                              ))}
                            </div>
                            <p className="mt-2 text-sm text-gray-500">Confidence: {abTestResults.confidence}</p>
                          </div>
                        )}
                      </div>

                      {/* CRM Webhook Configuration */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Globe className="w-5 h-5 text-blue-600" />
                          <h3 className="font-heading font-semibold">CRM Webhook Integration</h3>
                        </div>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                              <Label className="text-sm text-gray-600 mb-1.5 block">Webhook URL</Label>
                              <Input 
                                placeholder="https://your-crm.com/webhook/leads"
                                value={crmWebhookUrl}
                                onChange={(e) => setCrmWebhookUrl(e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-sm text-gray-600 mb-1.5 block">CRM Type</Label>
                              <Select value={crmType} onValueChange={setCrmType}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  <SelectItem value="generic">Generic</SelectItem>
                                  <SelectItem value="salesforce">Salesforce</SelectItem>
                                  <SelectItem value="hubspot">HubSpot</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button onClick={testCrmWebhook} disabled={!crmWebhookUrl}>
                              Test Webhook
                            </Button>
                            {crmTestResult && (
                              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${crmTestResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {crmTestResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                                {crmTestResult.success ? 'Connection successful!' : `Error: ${crmTestResult.error || crmTestResult.status_code}`}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Configure your CRM webhook to receive lead data in real-time. Supports Salesforce, HubSpot, or any custom webhook endpoint.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Click "Refresh" to load analytics data</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Lead Detail Modal */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Lead Details
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">
                    {selectedLead['contact.first_name']} {selectedLead['contact.last_name']}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedLead['contact.email']}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedLead['contact.phone'] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Country</p>
                  <p className="font-medium">{selectedLead['contact.country']}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Language</p>
                  <p className="font-medium">{selectedLead['contact.language'] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium">{selectedLead['contact.company'] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Role</p>
                  <p className="font-medium">{selectedLead['contact.role'] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">LinkedIn</p>
                  <p className="font-medium">
                    {selectedLead['contact.linkedin'] ? (
                      <a href={selectedLead['contact.linkedin']} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        View Profile
                      </a>
                    ) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{selectedLead.lead_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {new Date(selectedLead.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {selectedLead.lead_type === 'investor' && (
                <div className="border-t pt-4">
                  <h4 className="font-heading font-semibold mb-3">Investor Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Investor Type</p>
                      <p className="font-medium">{selectedLead['investor.type']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Focus</p>
                      <p className="font-medium">{selectedLead['investor.focus']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ticket Size</p>
                      <p className="font-medium">{selectedLead['investor.ticket_size']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Investment Range</p>
                      <p className="font-medium">{selectedLead['investor.range']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Time Horizon</p>
                      <p className="font-medium">{selectedLead['investor.time_horizon'] || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sectors</p>
                      <p className="font-medium">
                        {selectedLead['investor.sectors']?.join(', ') || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Geography</p>
                      <p className="font-medium">
                        {selectedLead['investor.geography']?.join(', ') || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedLead.lead_type === 'company' && (
                <div className="border-t pt-4">
                  <h4 className="font-heading font-semibold mb-3">Company Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Website</p>
                      <p className="font-medium">
                        {selectedLead['company.website'] ? (
                          <a href={selectedLead['company.website']} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {selectedLead['company.website']}
                          </a>
                        ) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Revenue Range</p>
                      <p className="font-medium">{selectedLead['company.revenue_range']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Team Size</p>
                      <p className="font-medium">{selectedLead['company.team_size']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Objective</p>
                      <p className="font-medium">{selectedLead['company.objective']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Timeline</p>
                      <p className="font-medium">{selectedLead['company.timeline']}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Budget Range</p>
                      <p className="font-medium">{selectedLead['company.budget_range']}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedLead['contact.message'] && (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground">Message</p>
                  <p className="font-medium mt-1 p-3 bg-muted rounded-lg">{selectedLead['contact.message']}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="Enter username"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2">
                      <Shield className="h-4 w-4" /> Admin (Full Access)
                    </span>
                  </SelectItem>
                  <SelectItem value="supervisor">
                    <span className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4" /> Supervisor (View Only)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateUser(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={createUser} className="flex-1 bg-navy">
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Result Modal */}
      <Dialog open={!!resetPasswordResult} onOpenChange={() => setResetPasswordResult(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Password Reset</DialogTitle>
          </DialogHeader>
          {resetPasswordResult && (
            <div className="space-y-4">
              <p>New password for <strong>{resetPasswordResult.username}</strong>:</p>
              <div className="p-4 bg-muted rounded-lg font-mono text-lg text-center select-all">
                {resetPasswordResult.password}
              </div>
              <p className="text-sm text-muted-foreground">
                Please save this password securely. It will not be shown again.
              </p>
              <Button onClick={() => setResetPasswordResult(null)} className="w-full">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Portal User Modal */}
      <Dialog open={showCreatePortalUser} onOpenChange={setShowCreatePortalUser}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Portal User</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Create a client account. They will be able to login with Google using this email.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email (must match Google account)</Label>
              <Input
                type="email"
                value={newPortalUser.email}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, email: e.target.value })}
                placeholder="client@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={newPortalUser.name}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newPortalUser.role} onValueChange={(v) => setNewPortalUser({ ...newPortalUser, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="investor">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Investor
                    </span>
                  </SelectItem>
                  <SelectItem value="company">
                    <span className="flex items-center gap-2">
                      <Building className="h-4 w-4" /> Company
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreatePortalUser(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={createPortalUser} 
                className="flex-1 bg-navy"
                disabled={!newPortalUser.email || !newPortalUser.name}
              >
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Portal Data Modal */}
      <Dialog open={showAddDataModal} onOpenChange={setShowAddDataModal}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Add {dataType.charAt(0).toUpperCase() + dataType.slice(1)}
            </DialogTitle>
            {selectedPortalUser && (
              <p className="text-sm text-muted-foreground">
                For: {selectedPortalUser.name} ({selectedPortalUser.role})
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Investment Form */}
            {dataType === 'investment' && (
              <>
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={investmentForm.company_name}
                    onChange={(e) => setInvestmentForm({...investmentForm, company_name: e.target.value})}
                    placeholder="e.g., TechCorp Inc."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      value={investmentForm.amount}
                      onChange={(e) => setInvestmentForm({...investmentForm, amount: e.target.value})}
                      placeholder="50000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={investmentForm.currency} onValueChange={(v) => setInvestmentForm({...investmentForm, currency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={investmentForm.date}
                      onChange={(e) => setInvestmentForm({...investmentForm, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={investmentForm.status} onValueChange={(v) => setInvestmentForm({...investmentForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="exited">Exited</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Returns % (optional, for exited)</Label>
                  <Input
                    type="number"
                    value={investmentForm.returns_percentage}
                    onChange={(e) => setInvestmentForm({...investmentForm, returns_percentage: e.target.value})}
                    placeholder="25"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input
                    value={investmentForm.notes}
                    onChange={(e) => setInvestmentForm({...investmentForm, notes: e.target.value})}
                    placeholder="Additional notes..."
                  />
                </div>
              </>
            )}

            {/* Deal Form */}
            {dataType === 'deal' && (
              <>
                <div className="p-3 bg-gold/10 rounded-lg text-sm text-gold border border-gold/20">
                  <strong>Note:</strong> Deals are visible to all active investors
                </div>
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={dealForm.title}
                    onChange={(e) => setDealForm({...dealForm, title: e.target.value})}
                    placeholder="e.g., Series A - GreenTech"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={dealForm.company_name}
                    onChange={(e) => setDealForm({...dealForm, company_name: e.target.value})}
                    placeholder="Company name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sector *</Label>
                    <Input
                      value={dealForm.sector}
                      onChange={(e) => setDealForm({...dealForm, sector: e.target.value})}
                      placeholder="e.g., FinTech"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stage *</Label>
                    <Input
                      value={dealForm.stage}
                      onChange={(e) => setDealForm({...dealForm, stage: e.target.value})}
                      placeholder="e.g., Series A"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Raise (€) *</Label>
                    <Input
                      type="number"
                      value={dealForm.target_raise}
                      onChange={(e) => setDealForm({...dealForm, target_raise: e.target.value})}
                      placeholder="1000000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min. Ticket (€) *</Label>
                    <Input
                      type="number"
                      value={dealForm.minimum_ticket}
                      onChange={(e) => setDealForm({...dealForm, minimum_ticket: e.target.value})}
                      placeholder="50000"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Input
                    value={dealForm.description}
                    onChange={(e) => setDealForm({...dealForm, description: e.target.value})}
                    placeholder="Brief description of the opportunity..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={dealForm.status} onValueChange={(v) => setDealForm({...dealForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="closing_soon">Closing Soon</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Deadline (optional)</Label>
                    <Input
                      type="date"
                      value={dealForm.deadline}
                      onChange={(e) => setDealForm({...dealForm, deadline: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Task Form */}
            {dataType === 'task' && (
              <>
                <div className="space-y-2">
                  <Label>Task Title *</Label>
                  <Input
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                    placeholder="e.g., Submit Q4 Report"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                    placeholder="Additional details..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Milestone Form */}
            {dataType === 'milestone' && (
              <>
                <div className="space-y-2">
                  <Label>Milestone Title *</Label>
                  <Input
                    value={milestoneForm.title}
                    onChange={(e) => setMilestoneForm({...milestoneForm, title: e.target.value})}
                    placeholder="e.g., Launch MVP"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={milestoneForm.description}
                    onChange={(e) => setMilestoneForm({...milestoneForm, description: e.target.value})}
                    placeholder="Describe the milestone..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Due Date *</Label>
                    <Input
                      type="date"
                      value={milestoneForm.due_date}
                      onChange={(e) => setMilestoneForm({...milestoneForm, due_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={milestoneForm.status} onValueChange={(v) => setMilestoneForm({...milestoneForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Progress: {milestoneForm.progress_percentage}%</Label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={milestoneForm.progress_percentage}
                    onChange={(e) => setMilestoneForm({...milestoneForm, progress_percentage: parseInt(e.target.value)})}
                    className="w-full"
                  />
                </div>
              </>
            )}

            {/* KPI Form */}
            {dataType === 'kpi' && (
              <>
                <div className="space-y-2">
                  <Label>KPI Name *</Label>
                  <Input
                    value={kpiForm.name}
                    onChange={(e) => setKpiForm({...kpiForm, name: e.target.value})}
                    placeholder="e.g., Monthly Revenue"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Current Value *</Label>
                    <Input
                      type="number"
                      value={kpiForm.value}
                      onChange={(e) => setKpiForm({...kpiForm, value: e.target.value})}
                      placeholder="50000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target *</Label>
                    <Input
                      type="number"
                      value={kpiForm.target}
                      onChange={(e) => setKpiForm({...kpiForm, target: e.target.value})}
                      placeholder="100000"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unit *</Label>
                    <Input
                      value={kpiForm.unit}
                      onChange={(e) => setKpiForm({...kpiForm, unit: e.target.value})}
                      placeholder="e.g., €, users, %"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Period *</Label>
                    <Input
                      value={kpiForm.period}
                      onChange={(e) => setKpiForm({...kpiForm, period: e.target.value})}
                      placeholder="e.g., Q4 2024"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Trend</Label>
                  <Select value={kpiForm.trend} onValueChange={(v) => setKpiForm({...kpiForm, trend: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="up">Up ↑</SelectItem>
                      <SelectItem value="down">Down ↓</SelectItem>
                      <SelectItem value="stable">Stable →</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Document Form */}
            {dataType === 'document' && (
              <>
                <div className="space-y-2">
                  <Label>File Name *</Label>
                  <Input
                    value={documentForm.filename}
                    onChange={(e) => setDocumentForm({...documentForm, filename: e.target.value})}
                    placeholder="e.g., Q4_Report_2024.pdf"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={documentForm.category} onValueChange={(v) => setDocumentForm({...documentForm, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="report">Report</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="presentation">Presentation</SelectItem>
                        <SelectItem value="legal">Legal</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>File Type</Label>
                    <Select value={documentForm.file_type} onValueChange={(v) => setDocumentForm({...documentForm, file_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="pdf">PDF</SelectItem>
                        <SelectItem value="xlsx">Excel</SelectItem>
                        <SelectItem value="docx">Word</SelectItem>
                        <SelectItem value="pptx">PowerPoint</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    value={documentForm.description}
                    onChange={(e) => setDocumentForm({...documentForm, description: e.target.value})}
                    placeholder="Brief description..."
                  />
                </div>
              </>
            )}

            {/* Meeting Form */}
            {dataType === 'meeting' && (
              <>
                <div className="space-y-2">
                  <Label>Meeting Title *</Label>
                  <Input
                    value={meetingForm.title}
                    onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                    placeholder="e.g., Quarterly Review"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    value={meetingForm.description}
                    onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                    placeholder="Meeting agenda..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={meetingForm.date}
                      onChange={(e) => setMeetingForm({...meetingForm, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={meetingForm.time}
                      onChange={(e) => setMeetingForm({...meetingForm, time: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration (min)</Label>
                    <Input
                      type="number"
                      value={meetingForm.duration_minutes}
                      onChange={(e) => setMeetingForm({...meetingForm, duration_minutes: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={meetingForm.location}
                      onChange={(e) => setMeetingForm({...meetingForm, location: e.target.value})}
                      placeholder="Office / Virtual"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Meeting Link (optional)</Label>
                  <Input
                    value={meetingForm.meeting_link}
                    onChange={(e) => setMeetingForm({...meetingForm, meeting_link: e.target.value})}
                    placeholder="https://zoom.us/..."
                  />
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowAddDataModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={addPortalData} 
                className="flex-1 bg-navy"
                disabled={dataFormLoading}
              >
                {dataFormLoading ? 'Adding...' : `Add ${dataType.charAt(0).toUpperCase() + dataType.slice(1)}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Data Type Button Component
const DataTypeButton = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
      active 
        ? 'border-navy bg-navy/5' 
        : 'border-transparent bg-white hover:border-navy/20'
    }`}
  >
    <Icon className={`w-6 h-6 ${active ? 'text-navy' : 'text-muted-foreground'}`} />
    <span className={`text-sm font-medium ${active ? 'text-navy' : 'text-muted-foreground'}`}>{label}</span>
    {badge && <span className="text-xs text-muted-foreground">{badge}</span>}
  </button>
);

export default AdminDashboard;
